import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Alert } from "antd";
import {
  defaultShipmentStatusBoardColumns,
  groupShipmentsByStatusLanes,
} from "../../../shipment/constants/shipmentBoardStatusLanes.js";
import {
  readShipmentBoardHideEmptyLanesMode,
  writeShipmentBoardHideEmptyLanesMode,
} from "../../../shipment/constants/shipmentCombinedBoard.js";
import { readShipmentTransportFilter } from "../../../shipment/constants/shipmentBoardLaneMode.js";
import { ListView } from "../components/ListView/ListView.jsx";
import { ListBoardPagination } from "../components/ListView/ListBoardPagination.jsx";
import { BoardRelationshipGraphDrawer } from "../components/Board/BoardRelationshipGraphDrawer.jsx";
import { BoardView } from "../components/BoardView/BoardView.jsx";
import { NotesDrawerShell } from "../components/Notes/NotesDrawerShell.jsx";
import { FilesDrawerShell } from "../components/PurchaseOrderDetails/Files/FilesDrawerShell.jsx";
import { CreateView } from "../components/CreateView/CreateView.jsx";
import { OptionsForView } from "../components/OptionsForView/OptionsForView.jsx";
import { useDispatch, useSelector } from "react-redux";
import { useGetConsignmentsQuery } from "../../../../store/apiSlice/shipmentApiSlice.js";
import { useCreateItemsPerPageMutation } from "../../../../store/apiSlice/userApiSlice.js";
import {
  useGetCurrentViewDetailsQuery,
  useGetFilterConsignmentsByRuleQuery,
  useGetMilestonesQuery,
  useGetViewQuery,
} from "../../../../store/apiSlice/newInterfaceApiSlice.js";
import "./Shipment.css";
import { mapItemPerPageWithModule, orderTypeForUrl } from "../utils/tableDataElements.js";
import { withStoredPageSize, writeListPageSize } from "../utils/listPageSizePreference.js";
import { TopBar } from "../../components/TopBar/TopBar.jsx";
import {
  getCurrentViewFromLocals,
  setListOfStatusForDefaultView,
} from "../../../../store/slice/newInterfaceSlice.js";
import { closeCreateView, handleCheckedOrder } from "../Constants/util.js";
import { ListViewLoading } from "../../ListView/ListViewLoading/ListViewLoading.jsx";

/**
 * Renders the Consignment list or board using the selected saved view.
 * Resolves local preferences and view metadata before enabling list requests.
 *
 * @return {React.ReactElement} The Consignment page or its loading indicator.
 */
const Shipment = () => {
  const [selectedOrder, setSelectedOrder] = useState([]);
  const [isOpenCreateView, setIsOpenCreateView] = useState({ isOpen: false, isEditMode: false });
  const [isOpenNotes, setIsOpenNotes] = useState(false);
  const [isOpenFiles, setIsOpenFiles] = useState(false);
  const [orderIdForNotes, setOrderIdForNotes] = useState(null);
  const [orderIdForFiles, setOrderIdForFiles] = useState(null);
  const [graphRecord, setGraphRecord] = useState(null);

  const dispatch = useDispatch();
  const orderType = "shipment";
  useGetCurrentViewDetailsQuery();
  const { consignment: currentViewDetails } = useSelector(state => state.newInterface.currentView);

  const statusOfViewFromLocal = currentViewDetails
    ? currentViewDetails?.isShowBoardView
      ? true
      : false
    : false;
  const [boardView, setBoardView] = useState(statusOfViewFromLocal);
  const [selectedCurrentView, setSelectedCurrentView] = useState({});
  const [preferencesLoaded, setPreferencesLoaded] = useState(false);
  const [hideEmptyLanesMode, setHideEmptyLanesMode] = useState(() =>
    readShipmentBoardHideEmptyLanesMode(),
  );
  const [transportMode, setTransportMode] = useState(() => readShipmentTransportFilter());
  const { consignmentsParams } = useSelector(state => state.shipment);
  const [consignmentsParamsLocal, setConsignmentsParamsLocal] = useState(() =>
    withStoredPageSize(orderType, consignmentsParams),
  );
  const [updatePerPage] = useCreateItemsPerPageMutation();

  const { data: availableViews, isLoading: viewLoading } = useGetViewQuery(
    new URLSearchParams(orderTypeForUrl["shipment"]).toString(),
  );
  const {
    isLoading: milestoneLoading,
    isError: milestoneError,
    refetch: reloadMilestones,
  } = useGetMilestonesQuery(new URLSearchParams(orderTypeForUrl[orderType]).toString());
  // Cached view metadata can arrive before local preferences and selection resolve.
  const resolvingView = viewLoading || !preferencesLoaded || !selectedCurrentView.value;

  const defaultQuery = useGetConsignmentsQuery(
    new URLSearchParams(consignmentsParamsLocal).toString(),
    {
      refetchOnMountOrArgChange: true,
      skip: resolvingView || selectedCurrentView.is_view_rule_exists,
    },
  );
  const ruleQuery = useGetFilterConsignmentsByRuleQuery(
    {
      id: selectedCurrentView?.id,
      params: new URLSearchParams(consignmentsParamsLocal).toString(),
    },
    {
      refetchOnMountOrArgChange: true,
      skip: resolvingView || !(selectedCurrentView.id && selectedCurrentView.is_view_rule_exists),
    },
  );
  const { allViews: allViewsFromServer } = useSelector(state => state.newInterface);
  const activeQuery = selectedCurrentView.is_view_rule_exists ? ruleQuery : defaultQuery;
  const previousPageRef = useRef({ results: [], count: 0 });
  const [hasLoadedList, setHasLoadedList] = useState(false);
  if (activeQuery.currentData) {
    previousPageRef.current = {
      results: activeQuery.currentData.results || [],
      count: activeQuery.currentData.count || 0,
    };
  }
  const shipmentOrders = useMemo(() => {
    if (activeQuery.currentData) {
      return activeQuery.currentData.results || [];
    }
    return hasLoadedList ? previousPageRef.current.results : [];
  }, [activeQuery.currentData, hasLoadedList]);
  const totalItems = activeQuery.currentData
    ? activeQuery.currentData.count || 0
    : hasLoadedList
      ? previousPageRef.current.count
      : 0;
  const allOrderNames = useMemo(() => shipmentOrders.map(order => order.name), [shipmentOrders]);
  const defaultStatusColumns = useMemo(
    () => defaultShipmentStatusBoardColumns(shipmentOrders),
    [shipmentOrders]
  );

  const savedStatusColumns = useMemo(() => {
    if (selectedCurrentView?.value === "Default View") {
      return defaultStatusColumns;
    }
    return allViewsFromServer?.boardView?.status?.allViews?.[selectedCurrentView?.value] || [];
  }, [selectedCurrentView?.value, defaultStatusColumns, allViewsFromServer?.boardView?.status?.allViews]);

  const filteredOrderByStatus = useMemo(() => {
    if (
      selectedCurrentView?.sub_type === "milestone" ||
      selectedCurrentView?.sub_type === "combined"
    ) {
      return {};
    }
    return groupShipmentsByStatusLanes(shipmentOrders, savedStatusColumns);
  }, [selectedCurrentView?.sub_type, shipmentOrders, savedStatusColumns]);

  useEffect(() => {
    if (
      selectedCurrentView?.value === "Default View" &&
      selectedCurrentView?.sub_type !== "milestone" &&
      selectedCurrentView?.sub_type !== "combined"
    ) {
      dispatch(setListOfStatusForDefaultView(defaultStatusColumns));
    }
  }, [dispatch, defaultStatusColumns, selectedCurrentView?.value, selectedCurrentView?.sub_type]);

  useEffect(() => {
    if (activeQuery.currentData) {
      setHasLoadedList(true);
    }
  }, [activeQuery.currentData]);

  const columnWidth = useMemo(() => {
    if (selectedCurrentView.value === "Default View") {
      return currentViewDetails?.column_width_for_default;
    }

    const completeListOfViews = allViewsFromServer?.completeListOfViews;

    if (!selectedCurrentView?.id || !completeListOfViews?.length) {
      return null;
    }

    const findCurrentView = completeListOfViews.find(item => item.id === selectedCurrentView?.id);
    return findCurrentView?.column_width || null;
    // eslint-disable-next-line
  }, [allViewsFromServer?.completeListOfViews, selectedCurrentView.id, currentViewDetails]);

  useEffect(() => {
    dispatch(getCurrentViewFromLocals());
    setPreferencesLoaded(true);
    // eslint-disable-next-line
  }, []);

  useEffect(() => {
    if (!preferencesLoaded || viewLoading) return;

    if (currentViewDetails && availableViews?.length > 0) {
      const currentViewId = currentViewDetails.isShowBoardView
        ? currentViewDetails.boardView.id
        : currentViewDetails.listView.id;
      const findCurrentView = availableViews.find(item => item.id === currentViewId);

      if (findCurrentView) {
        setBoardView(currentViewDetails.isShowBoardView);
        const savedView = currentViewDetails.isShowBoardView
          ? currentViewDetails.boardView
          : currentViewDetails.listView;
        setSelectedCurrentView({
          ...savedView,
          value: findCurrentView.name,
          sub_type: findCurrentView.sub_type?.id || null,
          is_view_rule_exists: Boolean(findCurrentView.filter_criteria),
        });
        return;
      }
    }
    setBoardView(Boolean(currentViewDetails?.isShowBoardView));
    setSelectedCurrentView({
      value: "Default View",
      sub_type: currentViewDetails?.isShowBoardView ? "status" : null,
    });
    // eslint-disable-next-line
  }, [currentViewDetails, availableViews, preferencesLoaded, viewLoading]);

  useEffect(() => {
    setSelectedOrder([]);
  }, [selectedCurrentView.id, boardView, consignmentsParamsLocal]);

  useEffect(() => {
    setConsignmentsParamsLocal(params => {
      const next = { ...params, offset: 0 };
      if (transportMode === "all") {
        const { transportation_mode: _removed, ...rest } = next;
        return rest;
      }
      return { ...next, transportation_mode: transportMode };
    });
  }, [transportMode]);

  /**
   * Selects a view starting at its first page.
   * @param {Object} view The saved or default view.
   * @return {void}
   */
  const handleViewChange = view => {
    setConsignmentsParamsLocal(params => ({ ...params, offset: 0 }));
    setSelectedCurrentView(view);
  };

  /**
   * Switches display modes without retaining a hidden list-page offset.
   * @param {boolean} enabled Whether to show the board.
   * @return {void}
   */
  const handleBoardViewChange = enabled => {
    setConsignmentsParamsLocal(params => ({ ...params, offset: 0 }));
    setBoardView(enabled);
  };

  /**
   * Loads the requested board page using the list query's pagination contract.
   * @param {number} page The one-based page number.
   * @param {number} size The number of records per page.
   * @return {void}
   */
  const handleBoardPagination = (page, size) => {
    writeListPageSize(orderType, size);
    setConsignmentsParamsLocal(params => ({ ...params, limit: size, offset: (page - 1) * size }));
    const moduleKey = mapItemPerPageWithModule[orderType];
    if (moduleKey) {
      updatePerPage({ limit: { [moduleKey]: size } });
    }
  };

  const handleHideEmptyLanesModeChange = mode => {
    setHideEmptyLanesMode(mode);
    writeShipmentBoardHideEmptyLanesMode(mode);
  };

  const refreshList = useCallback(() => {
    activeQuery.refetch();
  }, [activeQuery]);

  const openNotesModal = useCallback(
    (id, orderName, is_note_available, open_task_count = 0) => {
      setOrderIdForNotes({ id, name: orderName, is_note_available, open_task_count });
      setIsOpenNotes(true);
    },
    [],
  );
  const closeNotesModal = useCallback(() => {
    setIsOpenNotes(false);
    setOrderIdForNotes(null);
  }, []);
  const openFilesDrawer = useCallback((id, orderName, recordDisplayName = "") => {
    setOrderIdForFiles({ id, name: orderName, recordDisplayName });
    setIsOpenFiles(true);
  }, []);
  const closeFilesDrawer = useCallback(() => {
    setIsOpenFiles(false);
    setOrderIdForFiles(null);
  }, []);
  const openGraphDrawer = useCallback((id, orderName) => {
    setGraphRecord({ id, name: orderName });
  }, []);
  const closeGraphDrawer = useCallback(() => {
    setGraphRecord(null);
  }, []);

  const recordActionHandlers = useMemo(
    () => ({
      onOpenFiles: openFilesDrawer,
      onOpenNotes: openNotesModal,
      onOpenGraph: openGraphDrawer,
    }),
    [openFilesDrawer, openGraphDrawer, openNotesModal],
  );

  const handleSelectOrder = e => {
    handleCheckedOrder(e, shipmentOrders, setSelectedOrder, selectedOrder);
  };

  return (
    <>
      <TopBar title="Purchase & Transfer Shipments" />
      {!hasLoadedList &&
      (resolvingView ||
        (boardView && milestoneLoading) ||
        (!activeQuery.currentData &&
          (activeQuery.isFetching || activeQuery.isUninitialized))) ? (
        <ListViewLoading />
      ) : activeQuery.isError || (boardView && milestoneError) ? (
        <Alert
          type="error"
          showIcon
          message="Unable to load consignments or milestones."
          action={
            <button
              onClick={() => {
                activeQuery.refetch();
                reloadMilestones();
              }}
            >
              Retry
            </button>
          }
        />
      ) : (
        <div className="shipment">
          <OptionsForView
            viewConfig={{
              boardView,
              setBoardView: handleBoardViewChange,
              selectedCurrentView,
              setSelectedCurrentView: handleViewChange,
              currentViewDetails,
              setIsOpenCreateView,
              hideEmptyLanesMode,
              onHideEmptyLanesModeChange: handleHideEmptyLanesModeChange,
              transportMode,
              onTransportModeChange: setTransportMode,
            }}
            orderConfig={{ orderType }}
            selectionConfig={{
              selectedOrderFromListview: selectedOrder,
              setSelectedOrder,
            }}
            callbacks={{
              getConsignmentByRule: activeQuery.refetch,
              reloadGetConsignments: activeQuery.refetch,
              setConsignmentsParamsLocal,
              listParams: consignmentsParamsLocal,
            }}
          />

          {isOpenCreateView.isOpen && (
            <CreateView
              closeCreateView={() => closeCreateView(setIsOpenCreateView)}
              boardView={boardView}
              editView={isOpenCreateView.isEditMode}
              selectedCurrentView={selectedCurrentView}
              orderType={orderType}
              setSelectedCurrentView={handleViewChange}
              />
          )}
          {boardView ? (
            <>
              <BoardView
                shipmentOrders={shipmentOrders}
                orderType={orderType}
                selectedCurrentView={selectedCurrentView}
                filteredOrderByStatus={filteredOrderByStatus}
                hideEmptyLanesMode={hideEmptyLanesMode}
                getConsignmentByRule={activeQuery.refetch}
                handleSelectOrder={handleSelectOrder}
              />
              <ListBoardPagination
                current={
                  Math.floor(
                    (consignmentsParamsLocal.offset || 0) / consignmentsParamsLocal.limit,
                  ) + 1
                }
                total={totalItems}
                pageSize={consignmentsParamsLocal.limit}
                disabled={activeQuery.isFetching}
                onChange={handleBoardPagination}
              />
            </>
          ) : (
            <>
              <NotesDrawerShell
                isOpen={isOpenNotes && Boolean(orderIdForNotes)}
                onClose={closeNotesModal}
                recordName={orderIdForNotes?.name}
                openTaskCount={orderIdForNotes?.open_task_count}
                key={orderIdForNotes?.id}
                orderId={orderIdForNotes?.id}
                isNoteAvailable={orderIdForNotes?.is_note_available}
                orderType={orderType}
                setOrderIdForNotes={setOrderIdForNotes}
                getConsignmentByRule={refreshList}
                selectedCurrentView={selectedCurrentView}
              />
              <FilesDrawerShell
                isOpen={isOpenFiles && Boolean(orderIdForFiles)}
                onClose={closeFilesDrawer}
                orderId={orderIdForFiles?.id}
                orderName={orderIdForFiles?.name}
                recordDisplayName={orderIdForFiles?.recordDisplayName}
                orderType={orderType}
                getConsignmentByRule={refreshList}
              />
              <BoardRelationshipGraphDrawer
                orderType={orderType}
                isOpen={Boolean(graphRecord)}
                onClose={closeGraphDrawer}
                record={graphRecord}
                onOpenFiles={openFilesDrawer}
                onOpenNotes={openNotesModal}
              />
              <ListView
                orderType={orderType}
                allOrderNames={allOrderNames}
                handleSelectOrder={handleSelectOrder}
                selectedOrderFromList={selectedOrder}
                tableHeaderList={allViewsFromServer.listView.allViews[selectedCurrentView?.value]}
                shipmentOrders={shipmentOrders}
                setConsignmentsParamsLocal={setConsignmentsParamsLocal}
                totalItems={totalItems}
                consignmentsParams={consignmentsParamsLocal}
                currentPage={
                  Math.floor(
                    (consignmentsParamsLocal.offset || 0) / consignmentsParamsLocal.limit,
                  ) + 1
                }
                widthForHeaders={columnWidth}
                selectedCurrentView={selectedCurrentView}
                onTrackingUpdated={activeQuery.refetch}
                recordActionHandlers={recordActionHandlers}
              />
            </>
          )}
        </div>
      )}
    </>
  );
};

export { Shipment };
